import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const PER_SLIDE = 1400; // ms

export const DropsIntro = ({ drops = [], team = [] }) => {
  const [show, setShow] = useState(false);
  const [i, setI] = useState(0);
  const started = useRef(false);

  const slides = useMemo(() => drops.slice(0, 3), [drops]);
  const nameById = useMemo(() => {
    const m = {};
    for (const t of team) m[t.id] = t;
    return m;
  }, [team]);

  useEffect(() => {
    if (started.current || !slides.length) return;
    started.current = true;
    setShow(true);
    document.body.style.overflow = "hidden";
    let idx = 0;
    const adv = setInterval(() => {
      idx += 1;
      if (idx >= slides.length) { clearInterval(adv); setShow(false); }
      else setI(idx);
    }, PER_SLIDE);
    return () => { clearInterval(adv); };
  }, [slides.length]);

  useEffect(() => { if (!show) document.body.style.overflow = ""; }, [show]);

  if (!slides.length) return null;
  const active = slides[Math.min(i, slides.length - 1)];
  const creator = nameById[active.creator_id];

  const skip = () => setShow(false);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="drops-intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center overflow-hidden"
        >
          <div className="absolute inset-0 grid-lines opacity-30" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(255,46,46,0.18),transparent_60%)]" />

          <button data-testid="drops-intro-skip" onClick={skip}
            className="absolute top-6 right-6 z-10 inline-flex items-center gap-2 border border-white/20 text-white/70 hover:text-white hover:border-volt px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-200">
            Skip <X className="w-3.5 h-3.5" />
          </button>

          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="relative font-mono text-xs tracking-[0.4em] uppercase text-volt mb-8 flex items-center gap-3">
            <span className="w-2 h-2 bg-volt animate-pulse" /> Fresh Drops
          </motion.span>

          <div className="relative w-[min(78vw,520px)] aspect-square">
            <AnimatePresence mode="wait">
              <motion.div key={active.id}
                initial={{ opacity: 0, scale: 0.86, rotate: -3 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 1.08 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 border border-white/10 bg-card overflow-hidden">
                {active.image_url
                  ? <img src={active.image_url} alt={active.title} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-white/20 font-display font-black text-6xl uppercase">{active.title?.[0] || "?"}</div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative mt-8 text-center px-6 min-h-[80px]">
            <AnimatePresence mode="wait">
              <motion.div key={active.id + "-txt"}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter uppercase text-white">{active.title}</h2>
                {creator && <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/60 mt-3">Dropped by @{creator.roblox_username || creator.name}</p>}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="absolute bottom-10 flex gap-2">
            {slides.map((_, idx) => (
              <div key={idx} className="h-[3px] w-10 bg-white/15 overflow-hidden">
                <motion.div className="h-full bg-volt"
                  initial={{ width: idx < i ? "100%" : "0%" }}
                  animate={{ width: idx < i ? "100%" : idx === i ? "100%" : "0%" }}
                  transition={{ duration: idx === i ? PER_SLIDE / 1000 : 0.2, ease: "linear" }} />
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
